import React, { Component, ErrorInfo, ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faRedo,
  faRefresh,
  faInfoCircle
} from "@fortawesome/free-solid-svg-icons";
import { Alert, Button, Code } from "@mantine/core";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * General error boundary component to catch JavaScript errors anywhere in the child component tree
 * and display a fallback UI instead of crashing the entire application.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Alert color="red" radius="md" my="md">
          <h5 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '8px' }} />
            Something went wrong
          </h5>
          <p style={{ marginBottom: '16px' }}>
            An unexpected error occurred while loading this section. 
            Please try refreshing the page or contact support if the problem persists.
          </p>
          
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <Button variant="outline" color="red" size="sm" onClick={this.handleRetry}>
              <FontAwesomeIcon icon={faRedo} style={{ marginRight: '4px' }} />
              Try Again
            </Button>
            
            <Button 
              variant="outline" 
              color="gray" 
              size="sm" 
              onClick={() => window.location.reload()}
            >
              <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '4px' }} />
              Refresh Page
            </Button>
          </div>

          {this.props.showDetails && this.state.error && (
            <details style={{ marginTop: '16px' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--mantine-color-dimmed)', fontSize: '0.875rem' }}>
                <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '4px' }} />
                Error Details (for debugging)
              </summary>
              <Code block mt="sm" p="sm" style={{ fontSize: '0.75rem' }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </Code>
            </details>
          )}
        </Alert>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
